import React, { useState, useEffect } from 'react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const getAuthHeaders = () => ({
  Authorization: `Bearer ${localStorage.getItem('adminToken')}`
});

const NewsSourcesManagement = () => {
  const [sources, setSources] = useState([]);
  const [loading, setLoading] = useState(true);
  const [notification, setNotification] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [parsing, setParsing] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    url: '',
    rss_url: '',
    parsing_type: 'rss',
    html_selectors: {
      news_item: 'article, .news-item',
      title: 'h2, h3, .title',
      link: 'a',
      preview: 'p, .preview',
      date: '.date, time',
      content: 'article, .content'
    },
    category: 'general',
    is_active: true
  });

  useEffect(() => {
    loadSources();
  }, []);

  const loadSources = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/admin/news-sources`, {
        headers: getAuthHeaders()
      });
      setSources(response.data);
    } catch (error) {
      showNotification('Ошибка загрузки источников', 'error');
    } finally {
      setLoading(false);
    }
  };

  const showNotification = (message, type = 'success') => {
    setNotification({ message, type });
    setTimeout(() => setNotification(null), 3000);
  };

  const openModal = (source = null) => {
    if (source) {
      setEditing(source);
      setFormData({
        name: source.name,
        url: source.url,
        rss_url: source.rss_url || '',
        parsing_type: source.parsing_type || 'html',
        html_selectors: source.html_selectors || {
          news_item: 'article',
          title: 'h2',
          link: 'a',
          preview: 'p',
          date: 'time',
          content: 'article'
        },
        category: source.category,
        is_active: source.is_active
      });
    } else {
      setEditing(null);
      setFormData({
        name: '',
        url: '',
        rss_url: '',
        parsing_type: 'rss',
        html_selectors: {
          news_item: 'article, .news-item',
          title: 'h2, h3, .title',
          link: 'a',
          preview: 'p, .preview',
          date: '.date, time',
          content: 'article, .content'
        },
        category: 'general',
        is_active: true
      });
    }
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditing(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editing) {
        await axios.put(
          `${API_URL}/api/admin/news-sources/${editing.id}`,
          formData,
          { headers: getAuthHeaders() }
        );
        showNotification('Источник обновлен');
      } else {
        await axios.post(
          `${API_URL}/api/admin/news-sources`,
          formData,
          { headers: getAuthHeaders() }
        );
        showNotification('Источник создан');
      }
      closeModal();
      loadSources();
    } catch (error) {
      showNotification('Ошибка сохранения', 'error');
    }
  };

  const deleteSource = async (id) => {
    if (!window.confirm('Удалить этот источник?')) return;
    try {
      await axios.delete(`${API_URL}/api/admin/news-sources/${id}`, {
        headers: getAuthHeaders()
      });
      showNotification('Источник удален');
      loadSources();
    } catch (error) {
      showNotification('Ошибка удаления', 'error');
    }
  };

  const parseNews = async () => {
    if (!window.confirm('Запустить парсинг новостей со всех активных источников?')) return;
    setParsing(true);
    try {
      const response = await axios.post(
        `${API_URL}/api/admin/news-sources/parse`,
        {},
        { headers: getAuthHeaders() }
      );
      showNotification(
        `Парсинг завершен. Создано статей: ${response.data.articles_created}, обработано источников: ${response.data.sources_processed}`
      );
      loadSources();
    } catch (error) {
      showNotification('Ошибка парсинга новостей', 'error');
    } finally {
      setParsing(false);
    }
  };

  if (loading) {
    return <div className="p-6 text-center">Загрузка...</div>;
  }

  return (
    <div className="space-y-6">
      {/* Notification */}
      {notification && (
        <div className={`p-4 rounded-lg ${notification.type === 'error' ? 'bg-red-50 text-red-800' : 'bg-green-50 text-green-800'}`}>
          {notification.message}
        </div>
      )}

      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Источники новостей</h1>
          <p className="text-gray-600 mt-1">Управление RSS-источниками для автоматического парсинга новостей</p>
        </div>
        <div className="flex gap-3">
          <button
            onClick={parseNews}
            disabled={parsing}
            className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-lg font-medium transition disabled:opacity-50"
          >
            {parsing ? '⏳ Парсинг...' : '🔄 Запустить парсинг'}
          </button>
          <button
            onClick={() => openModal()}
            className="bg-orange-500 hover:bg-orange-600 text-white px-6 py-2 rounded-lg font-medium transition"
          >
            + Добавить источник
          </button>
        </div>
      </div>

      {/* Sources Table */}
      <div className="bg-white rounded-lg shadow-sm overflow-hidden">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Название</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">URL</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Тип</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Категория</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Статус</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Последний парсинг</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Действия</th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {sources.map((source) => (
              <tr key={source.id}>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="text-sm font-medium text-gray-900">{source.name}</div>
                </td>
                <td className="px-6 py-4">
                  <a href={source.url} target="_blank" rel="noopener noreferrer" className="text-sm text-blue-600 hover:underline">
                    {source.url}
                  </a>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <span className={`px-2 py-1 text-xs rounded-full ${source.parsing_type === 'html' ? 'bg-blue-100 text-blue-800' : 'bg-purple-100 text-purple-800'}`}>
                    {source.parsing_type === 'html' ? 'HTML' : 'RSS'}
                  </span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                  {source.category}
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <span className={`px-2 py-1 text-xs rounded-full ${source.is_active ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}`}>
                    {source.is_active ? 'Активен' : 'Неактивен'}
                  </span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                  {source.last_parsed ? new Date(source.last_parsed).toLocaleString('ru-RU') : 'Никогда'}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm space-x-3">
                  <button
                    onClick={() => openModal(source)}
                    className="text-blue-600 hover:text-blue-800"
                  >
                    Редактировать
                  </button>
                  <button
                    onClick={() => deleteSource(source.id)}
                    className="text-red-600 hover:text-red-800"
                  >
                    Удалить
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b">
              <h2 className="text-2xl font-bold text-gray-900">
                {editing ? 'Редактировать источник' : 'Добавить источник'}
              </h2>
            </div>

            <form onSubmit={handleSubmit} className="p-6 space-y-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Название *</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                  required
                  placeholder="Федеральная Таможенная Служба"
                  className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-orange-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">URL сайта *</label>
                <input
                  type="url"
                  value={formData.url}
                  onChange={(e) => setFormData({...formData, url: e.target.value})}
                  required
                  placeholder="https://customs.gov.ru/press/federal"
                  className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-orange-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Тип парсинга *</label>
                <select
                  value={formData.parsing_type}
                  onChange={(e) => setFormData({...formData, parsing_type: e.target.value})}
                  className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-orange-500"
                >
                  <option value="rss">RSS (рекомендуется)</option>
                  <option value="html">HTML</option>
                </select>
                <p className="text-xs text-gray-500 mt-1">RSS надёжнее. Прямой парсинг гос-сайтов (.gov.ru) недоступен из окружения — используйте RSS доступных агрегаторов (Альта-Софт, ТАСС и т.п.)</p>
              </div>

              {formData.parsing_type === 'rss' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">RSS URL *</label>
                  <input
                    type="url"
                    value={formData.rss_url}
                    onChange={(e) => setFormData({...formData, rss_url: e.target.value})}
                    placeholder="https://customs.gov.ru/rss"
                    className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-orange-500"
                  />
                  <p className="text-xs text-gray-500 mt-1">Ссылка на RSS-ленту новостей</p>
                </div>
              )}

              {formData.parsing_type === 'html' && (
                <div className="space-y-3 bg-gray-50 p-4 rounded-lg">
                  <p className="text-sm font-medium text-gray-700">CSS Селекторы для HTML парсинга:</p>
                  
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs text-gray-600 mb-1">Элемент новости *</label>
                      <input
                        type="text"
                        value={formData.html_selectors?.news_item || ''}
                        onChange={(e) => setFormData({...formData, html_selectors: {...formData.html_selectors, news_item: e.target.value}})}
                        placeholder="article, .news-item"
                        className="w-full px-3 py-2 text-sm border rounded focus:ring-2 focus:ring-orange-500"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-xs text-gray-600 mb-1">Заголовок *</label>
                      <input
                        type="text"
                        value={formData.html_selectors?.title || ''}
                        onChange={(e) => setFormData({...formData, html_selectors: {...formData.html_selectors, title: e.target.value}})}
                        placeholder="h2, h3, .title"
                        className="w-full px-3 py-2 text-sm border rounded focus:ring-2 focus:ring-orange-500"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-xs text-gray-600 mb-1">Ссылка *</label>
                      <input
                        type="text"
                        value={formData.html_selectors?.link || ''}
                        onChange={(e) => setFormData({...formData, html_selectors: {...formData.html_selectors, link: e.target.value}})}
                        placeholder="a"
                        className="w-full px-3 py-2 text-sm border rounded focus:ring-2 focus:ring-orange-500"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-xs text-gray-600 mb-1">Превью</label>
                      <input
                        type="text"
                        value={formData.html_selectors?.preview || ''}
                        onChange={(e) => setFormData({...formData, html_selectors: {...formData.html_selectors, preview: e.target.value}})}
                        placeholder="p, .preview"
                        className="w-full px-3 py-2 text-sm border rounded focus:ring-2 focus:ring-orange-500"
                      />
                    </div>
                  </div>
                  
                  <p className="text-xs text-gray-500 mt-2">
                    💡 Используйте инструменты разработчика браузера для определения CSS селекторов
                  </p>
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Категория *</label>
                <select
                  value={formData.category}
                  onChange={(e) => setFormData({...formData, category: e.target.value})}
                  required
                  className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-orange-500"
                >
                  <option value="general">Общие</option>
                  <option value="customs">Таможня</option>
                  <option value="radiofrequency">Радиочастоты</option>
                  <option value="import_export">Импорт/Экспорт</option>
                  <option value="marking">Маркировка</option>
                  <option value="certification">Сертификация</option>
                </select>
              </div>

              <div className="flex items-center">
                <input
                  type="checkbox"
                  checked={formData.is_active}
                  onChange={(e) => setFormData({...formData, is_active: e.target.checked})}
                  className="h-4 w-4 text-orange-600 focus:ring-orange-500 border-gray-300 rounded"
                />
                <label className="ml-2 block text-sm text-gray-900">Активен</label>
              </div>

              <div className="flex justify-end space-x-3 pt-4 border-t">
                <button
                  type="button"
                  onClick={closeModal}
                  className="px-6 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
                >
                  Отмена
                </button>
                <button
                  type="submit"
                  className="px-6 py-2 bg-orange-500 hover:bg-orange-600 text-white rounded-lg"
                >
                  {editing ? 'Сохранить' : 'Создать'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default NewsSourcesManagement;
